import { Location } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, ParamMap } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Debate, DebatesService, ModalComponent, PopUpService, QuestionBase, TextareaQuestion } from 'core/index';
import { TutorizationQuestionService } from '../../group-questions/services/tutorization-question.service';
import {
  DebateSeasonsWidget,
  DebateVotesWidget,
  DebateOpinionsWidget,
  DebateActionsWidget
} from './debate-detail-widgets';
import { Widget } from '@widgets/widget';
import { switchMap } from 'rxjs/operators';

@Component({
  selector: 'lms-debate-detail',
  templateUrl: './debate-detail.component.html',
  styleUrls: ['./debate-detail.component.scss']
})
export class DebateDetailComponent implements OnInit {
  public debate: Debate;
  public computedDebateData: any;
  public seasonsWidget: Widget;
  public votesWidget: Widget;
  public opinionsWidget: Widget;
  public actionsWidget: Widget;
  public questionInfo: any;
  @ViewChild('cancelDebateModal', { static: false }) public cancelDebateModal: ModalComponent;
  protected questions: QuestionBase<any>[] = [];

  constructor(
    private route: ActivatedRoute,
    private location: Location,
    private debatesService: DebatesService,
    private popUpService: PopUpService,
    private translateService: TranslateService,
    private groupQuestionsService: TutorizationQuestionService
  ) {}

  ngOnInit(): void {
    this.route.paramMap
      .pipe(switchMap((params: ParamMap) => this.debatesService.getDebate(params.get('debateId'))))
      .subscribe((debate) => {
        this.debate = debate;
        this.getDebateQuestionData(this.debate.groupQuestionId).then();
        const computedDebateData = this.debatesService.getComputedDebateData(this.debate);
        this.computedDebateData = computedDebateData;
        this.seasonsWidget = DebateSeasonsWidget(this.debate.id, computedDebateData);
        this.votesWidget = DebateVotesWidget(this.debate.id, computedDebateData);
        this.opinionsWidget = DebateOpinionsWidget(this.debate.id, computedDebateData);
        this.actionsWidget = DebateActionsWidget(this.debate.id, computedDebateData);

        this.debatesService.triggerCertifyDebateActionStream.subscribe((debateId) => {
          if (this.debate.id === debateId) {
            this.onCertifyDebateQuestion();
          }
        });
        this.debatesService.triggerCancelDebateActionStream.subscribe((debateId) => {
          if (this.debate.id === debateId) {
            this.onCancelDebateQuestion();
          }
        });
      });
  }

  getDebateQuestionData(id) {
    return this.groupQuestionsService.getQuestionData(id).then((data) => {
      this.questionInfo = data;
    });
  }

  onCertifyDebateQuestion() {
    this.popUpService
      .showConfirm({
        title: this.translateService.instant('debates.messages.certifyQuestion'),
        text: this.translateService.instant('agents.deleteMessage'),
        type: 'question',
        showCancelButton: true,
        confirmButtonText: 'actions.yes',
        cancelButtonText: 'actions.no'
      })
      .then(() => {
        const newStatus = this.computedDebateData.validStatuses.CERTIFIED;
        return this.updateDebateQuestionStatus({ newStatus });
      });
  }

  onCancelDebateQuestion() {
    this.openCancelDebateModal();
  }

  updateDebateQuestionStatus(data) {
    const groupQuestionId = this.computedDebateData.groupQuestionId;
    return this.debatesService
      .updateDebateQuestion(groupQuestionId, data)
      .then(() => {
        return this.debatesService.getDebate(this.debate.id).subscribe((debate) => {
          this.debate = debate;
          const computedData = this.debatesService.getComputedDebateData(debate);
          this.debatesService.debateDataSource.next(computedData);
        });
      })
      .catch((err) => {
        this.popUpService
          .showError({
            title: this.translateService.instant('debates.messages.error'),
            text: err.serverMessage
          })
          .then();
      });
  }

  public openCancelDebateModal(): void {
    this.questions = [this.setQuestions()];
    this.cancelDebateModal.open();
  }

  public closeCancelDebateModal(): void {
    this.cancelDebateModal.close();
  }

  public onSubmit(debateData: { reason: string }) {
    const newStatus = this.computedDebateData.validStatuses.CANCELLED;
    this.updateDebateQuestionStatus({ newStatus, explanation: debateData.reason }).then();
  }

  private setQuestions(): TextareaQuestion {
    return new TextareaQuestion({
      key: 'reason',
      label: 'debates.messages.reason',
      placeholder: 'debates.messages.cancelInputPlaceholder',
      validators: {
        required: true,
        maxLength: 2000
      },
      heightBox: 200,
      value: null
    });
  }
}
